import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
  Stack,
} from '@mui/material';

import { useClientBrowser } from '@client/components/hooks/useClientBrowser';
import useDialog from '@client/components/hooks/useDialog';
import { useSessionStorage } from '@client/components/hooks/useSessionStorage';
import { useAuth } from '@client/context/auth/AuthContext';
import { useSnackbarContext } from '@client/context/SnackbarContext';
import { type SessionStorageKeys } from '@customTypes/auth';

const VerifyEmailBanner = () => {
  const { isEmailVerified, sendAuthenticatedEmailVerificationRequest } = useAuth();
  const { storedValue, setValue: setNewStorageValue } = useSessionStorage<
    SessionStorageKeys,
    string
  >('auth:email-verify-has-closed-popup', '');
  const { handleSnackbarOpen } = useSnackbarContext();
  const [isLoading, setIsLoading] = useState(false);
  const { isShowing, handleOpen, handleClose } = useDialog();
  const { isMobile } = useClientBrowser();

  const hasClosedPopup = storedValue ? !!Number(storedValue) : false;

  useEffect(() => {
    if (!isEmailVerified && !hasClosedPopup) {
      handleOpen();
    }
  }, [isEmailVerified]);

  const handleEmailVerificationCloseBanner = () => {
    setNewStorageValue('1');
    handleClose();
  };

  const handleTriggerEmailVerificationEmail = async () => {
    try {
      setIsLoading(true);
      const { success, status } = await sendAuthenticatedEmailVerificationRequest();
      if (success) {
        handleSnackbarOpen('We hebben een verificatielink gestuurd naar je e-mailadres');
        handleEmailVerificationCloseBanner();
        return;
      }
      // Email address has already been verified
      if (status === 203) {
        handleSnackbarOpen('Je e-mailadres is al geverifieerd', 'info');
        handleEmailVerificationCloseBanner();
        return;
      }

      handleSnackbarOpen(
        'Er is iets misgegaan bij het verzenden van je verificatielink',
        'warning',
      );
      return;
    } catch (error: any) {
      handleSnackbarOpen(
        'Er is iets misgegaan bij het verzenden van je verificatielink',
        'warning',
      );
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!isEmailVerified && !hasClosedPopup && (
        <Container maxWidth='xl' disableGutters>
          <Snackbar
            open={isShowing}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            autoHideDuration={null}
            sx={{
              width: 'max-content',
              height: 'max-content',
              bottom: isMobile ? 12 : 12,
            }}>
            <Alert
              severity='info'
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              action={
                <Stack direction={'row'}>
                  {isLoading ? (
                    <CircularProgress
                      variant='indeterminate'
                      disableShrink
                      sx={{
                        animationDuration: '550ms',
                      }}
                      size={25}
                      thickness={4}
                    />
                  ) : (
                    <>
                      {!isMobile && (
                        <Button
                          variant='text'
                          color='primary'
                          size='small'
                          onClick={handleTriggerEmailVerificationEmail}>
                          Verifieer je e-mail
                        </Button>
                      )}

                      <IconButton
                        aria-label='close'
                        color='inherit'
                        sx={{ pr: 1 }}
                        onClick={handleEmailVerificationCloseBanner}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  )}
                </Stack>
              }>
              {!isMobile ? (
                'Je e-mail is nog niet geverifieerd.'
              ) : (
                <Button
                  variant='text'
                  color='primary'
                  size='small'
                  sx={{ p: 0 }}
                  onClick={handleTriggerEmailVerificationEmail}>
                  Verifieer je e-mail
                </Button>
              )}
            </Alert>
          </Snackbar>
        </Container>
      )}
    </>
  );
};

export default VerifyEmailBanner;
